import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { fetchOrderDetails, clearOrderDetails } from '../../redux/slices/orderSlice';
import axios from 'axios';
import { RefreshCcw, BadgeIndianRupee, Package, Clock } from 'lucide-react';
import CancellationModal from './CancellationModal';

const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000';

const OrderDetailsPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { orderDetails, loading, error } = useSelector((state) => state.orders);
  const [returnInfo, setReturnInfo] = useState(null);
  const [loadingReturn, setLoadingReturn] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [canCancel, setCanCancel] = useState(false);

  useEffect(() => {
    dispatch(clearOrderDetails());
    if (id) {
      dispatch(fetchOrderDetails(id));
      fetchReturnInfo(id);
    }
  }, [dispatch, id]);

  // Check cancellation eligibility and update countdown timer
  useEffect(() => {
    if (!orderDetails) return;

    const checkCancellationEligibility = () => {
      // Only allow cancellation for Processing orders
      if (orderDetails.status !== 'Processing') {
        setCanCancel(false);
        setTimeRemaining(null);
        return;
      }

      const orderTime = new Date(orderDetails.createdAt).getTime();
      const currentTime = new Date().getTime();
      const twoHoursInMs = 2 * 60 * 60 * 1000;
      const elapsed = currentTime - orderTime;
      const remaining = twoHoursInMs - elapsed;

      if (remaining > 0) {
        setCanCancel(true);
        setTimeRemaining(remaining);
      } else {
        setCanCancel(false);
        setTimeRemaining(null);
      }
    };

    checkCancellationEligibility();
    const interval = setInterval(checkCancellationEligibility, 1000);

    return () => clearInterval(interval);
  }, [orderDetails]);

  const fetchReturnInfo = async (orderId) => {
    try {
      setLoadingReturn(true);
      const { data } = await axios.get(`${API_BASE}/api/returns/order/${orderId}`);
      setReturnInfo(data);
    } catch (err) {
      // No return found for this order - that's okay
      if (err.response?.status !== 404) {
        console.error('Failed to fetch return info:', err);
      }
    } finally {
      setLoadingReturn(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processing': return 'bg-yellow-100 border-2 border-yellow-800 text-yellow-800';
      case 'Shipped': return 'bg-blue-100 border-2 border-blue-800 text-blue-800';
      case 'Delivered': return 'bg-green-100 border-2 border-green-800 text-green-800';
      case 'Cancelled': return 'bg-red-100 border-2 border-red-800 text-red-800';
      default: return 'bg-gray-100 border-2 border-gray-800 text-gray-800';
    }
  };

  const getReturnStatusColor = (status) => {
    switch (status) {
      case 'Requested': return 'bg-yellow-100 border-2 border-yellow-600 text-yellow-800';
      case 'Under Review': return 'bg-blue-100 border-2 border-blue-600 text-blue-800';
      case 'Approved': return 'bg-green-100 border-2 border-green-600 text-green-800';
      case 'Item Received': return 'bg-purple-100 border-2 border-purple-600 text-purple-800';
      case 'Refund Processing': return 'bg-indigo-100 border-2 border-indigo-600 text-indigo-800';
      case 'Refunded': return 'bg-green-100 border-2 border-green-700 text-green-900';
      case 'Rejected': return 'bg-red-100 border-2 border-red-600 text-red-800';
      case 'Cancelled': return 'bg-gray-100 border-2 border-gray-600 text-gray-800';
      default: return 'bg-gray-100 border-2 border-gray-600 text-gray-800';
    }
  };

  const getPaymentStatusDisplay = (order) => {
    if (order.paymentStatus) {
      return order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1);
    }
    return order.isPaid ? 'Paid' : 'Unpaid';
  };

  const formatTimeRemaining = (ms) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const handleCancellationSuccess = (updatedOrder) => {
    // Refresh the order details
    dispatch(fetchOrderDetails(id));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">Loading order details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
        </div>
        <Link to="/my-orders" className="inline-block mt-4 text-black hover:underline">
          Back to My Orders
        </Link>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <p className="text-center text-gray-500 text-lg">No order details found</p>
        <Link to="/my-orders" className="inline-block mt-4 text-black hover:underline">
          Back to My Orders
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl md:text-3xl font-bold">Order Details</h2>
        <Link to="/my-orders" className="text-black hover:underline flex items-center">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to My Orders
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Order Summary Header */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-900">
                Order #{orderDetails._id}
              </h3>
              <p className="text-gray-600 mt-1">
                Placed on {new Date(orderDetails.createdAt).toLocaleDateString('en-IN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <div className="flex flex-col items-start sm:items-end mt-4 sm:mt-0 space-y-2">
              <span className={`px-3 py-1 rounded-lg text-sm font-medium ${getStatusColor(orderDetails.status)}`}>
                {orderDetails.status}
              </span>
              <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                orderDetails.isPaid || orderDetails.paymentStatus === 'paid'
                  ? 'bg-green-100 border-2 border-green-800 text-green-800' 
                  : 'bg-red-100 border-2 border-red-800 text-red-800'
              }`}>
                {getPaymentStatusDisplay(orderDetails)}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Cancellation Request Button (only for Processing orders within 2 hours) */}
          {canCancel && timeRemaining && (
            <div className="bg-orange-50 border border-orange-200 p-4 mb-6 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                    <Clock className="text-orange-600" size={18} />
                    Need to cancel this order?
                  </h4>
                  <p className="text-orange-800 text-sm mb-2">
                    You can request cancellation within 2 hours of placing your order.
                  </p>
                  <p className="text-orange-700 text-xs mb-3">
                    Time remaining: <strong>{formatTimeRemaining(timeRemaining)}</strong>
                  </p>
                  <button
                    onClick={() => setShowCancellationModal(true)}
                    className="text-orange-700 text-sm font-medium hover:underline"
                  >
                    Request Order Cancellation
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Return Status Section */}
          {returnInfo && (
            <div className={`border-l-4 p-4 mb-6 rounded-lg ${
              returnInfo.status === 'Refunded' 
                ? 'bg-green-50 border-green-500'
                : returnInfo.status === 'Rejected'
                ? 'bg-red-50 border-red-500'
                : 'bg-blue-50 border-blue-500'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <RefreshCcw className="text-blue-600" size={20} />
                    Return Request Status
                  </h4>
                  <div className="mb-3">
                    <span className={`inline-block px-3 py-1 rounded-lg text-sm font-medium ${getReturnStatusColor(returnInfo.status)}`}>
                      {returnInfo.status}
                    </span>
                  </div>

                  {/* Return Timeline */}
                  <div className="space-y-2 text-sm mb-4">
                    <p className="text-gray-700">
                      <strong>Requested:</strong> {new Date(returnInfo.createdAt).toLocaleString('en-IN')}
                    </p>
                    {returnInfo.approvedAt && (
                      <p className="text-green-700">
                        <strong>Approved:</strong> {new Date(returnInfo.approvedAt).toLocaleString('en-IN')}
                      </p>
                    )}
                    {returnInfo.itemReceivedAt && (
                      <p className="text-purple-700">
                        <strong>Item Received:</strong> {new Date(returnInfo.itemReceivedAt).toLocaleString('en-IN')}
                      </p>
                    )}
                    {returnInfo.refundedAt && (
                      <p className="text-green-700">
                        <strong>Refunded:</strong> {new Date(returnInfo.refundedAt).toLocaleString('en-IN')}
                      </p>
                    )}
                    {returnInfo.rejectedAt && (
                      <p className="text-red-700">
                        <strong>Rejected:</strong> {new Date(returnInfo.rejectedAt).toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>

                  {/* Refund Details */}
                  {(returnInfo.status === 'Refunded' || 
                    returnInfo.status === 'Refund Processing' || 
                    returnInfo.refundAmount) && (
                    <div className="bg-white p-4 rounded-lg mb-3 border border-green-200">
                      <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <BadgeIndianRupee className="text-green-600" size={20} />
                        Refund Details
                      </h5>
                      <div className="space-y-2">
                        {returnInfo.refundAmount && (
                          <p className="text-sm text-gray-700">
                            <strong>Amount:</strong> ₹{returnInfo.refundAmount}
                          </p>
                        )}
                        {returnInfo.refundMethod && (
                          <p className="text-sm text-gray-700">
                            <strong>Method:</strong> {returnInfo.refundMethod}
                          </p>
                        )}
                        {returnInfo.refundTransactionId && (
                          <p className="text-sm text-gray-700">
                            <strong>Transaction ID:</strong> {returnInfo.refundTransactionId}
                          </p>
                        )}
                        {returnInfo.refundedAt && (
                          <p className="text-sm text-gray-700">
                            <strong>Refund Date:</strong> {new Date(returnInfo.refundedAt).toLocaleString('en-IN', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                      {returnInfo.status === 'Refund Processing' && (
                        <p className="text-sm text-blue-600 mt-3 bg-blue-50 p-2 rounded">
                          ⏳ Your refund will be credited within 5-7 business days
                        </p>
                      )}
                      {returnInfo.status === 'Refunded' && (
                        <p className="text-sm text-green-600 mt-3 bg-green-50 p-2 rounded">
                          ✓ Refund completed successfully
                        </p>
                      )}
                    </div>
                  )}

                  {/* Return Tracking */}
                  {(returnInfo.returnTrackingNumber || returnInfo.returnTrackingLink) && (
                    <div className="bg-white p-4 rounded-lg mb-3 border border-blue-200">
                      <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Package className="text-blue-600" size={20} />
                        Return Shipment Tracking
                      </h5>
                      {returnInfo.returnTrackingNumber && (
                        <p className="text-sm text-gray-700 mb-3">
                          <strong>Tracking Number:</strong> {returnInfo.returnTrackingNumber}
                        </p>
                      )}
                      {returnInfo.returnTrackingLink && (
                        <a
                          href={returnInfo.returnTrackingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                        >
                          Track Return Shipment →
                        </a>
                      )}
                    </div>
                  )}

                  {/* Admin Note */}
                  {returnInfo.adminNote && (
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <strong>Note from Support:</strong> {returnInfo.adminNote}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Request Return Button (if eligible) */}
          {!returnInfo && 
           orderDetails.status === 'Delivered' && 
           orderDetails.deliveredAt &&
           (new Date() - new Date(orderDetails.deliveredAt)) / (1000 * 60 * 60 * 24) <= 7 && (
            <div className="bg-blue-50 border border-blue-200 p-4 mb-6 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2">
                    Return Available
                  </h4>
                  <p className="text-blue-800 text-sm mb-3">
                    You can request a return for this order within 7 days of delivery.
                  </p>
                  <Link
                    to={`/return?orderId=${orderDetails._id}`}
                    className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                  >
                    Request Return
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Rest of the existing code for tracking, cancellation, delivery status, etc. */}
          {orderDetails.status === 'Shipped' && orderDetails.trackingLink && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                    <span className="text-xl mr-2">📦</span>
                    Your Order Is On The Way!
                  </h4>
                  <p className="text-blue-800 text-sm mb-3">
                    Track your shipment to see real-time updates
                  </p>
                  {orderDetails.trackingNumber && (
                    <p className="text-sm text-blue-700 mb-3">
                      <strong>Tracking Number:</strong> {orderDetails.trackingNumber}
                    </p>
                  )}
                  <a
                    href={orderDetails.trackingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                  >
                    Track Shipment
                  </a>
                </div>
              </div>
            </div>
          )}

          {orderDetails.status === 'Cancelled' && orderDetails.cancellationReason && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
              <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                <span className="text-xl mr-2">✕</span>
                Order Cancelled
              </h4>
              <p className="text-red-800 text-sm mb-2">
                <strong>Reason:</strong>
              </p>
              <p className="text-red-700 text-sm bg-red-100 p-3 rounded">
                {orderDetails.cancellationReason}
              </p>
              {orderDetails.isPaid && (
                <p className="text-red-800 text-sm mt-3">
                  <strong>Refund Status:</strong> Your refund will be processed within 5-7 business days to your original payment method.
                </p>
              )}
            </div>
          )}

          {orderDetails.status === 'Delivered' && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                <span className="text-xl mr-2">✓</span>
                Order Delivered Successfully!
              </h4>
              <p className="text-green-800 text-sm">
                Delivered on {new Date(orderDetails.deliveredAt).toLocaleString('en-IN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}

          {/* Order Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Order Information</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium text-gray-900">Total Amount:</span> Rs. {orderDetails.totalPrice?.toFixed(2) || '0.00'}</p>
                <p><span className="font-medium text-gray-900">Payment Method:</span> {orderDetails.paymentMethod}</p>
                {orderDetails.paidAt && (
                  <p><span className="font-medium text-gray-900">Paid At:</span> {new Date(orderDetails.paidAt).toLocaleString()}</p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Customer Information</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium text-gray-900">Name:</span> {orderDetails.user?.name || 'N/A'}</p>
                <p><span className="font-medium text-gray-900">Email:</span> {orderDetails.user?.email || 'N/A'}</p>
                <p><span className="font-medium text-gray-900">Mobile:</span> {orderDetails.shippingAddress?.mobile || 'N/A'}</p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Shipping Information</h4>
              <div className="space-y-2 text-sm text-gray-600">
                {orderDetails.shippingAddress && (
                  <>
                    <p><span className="font-medium text-gray-900">Address:</span> {orderDetails.shippingAddress.address}</p>
                    <p><span className="font-medium text-gray-900">City:</span> {orderDetails.shippingAddress.city}</p>
                    <p><span className="font-medium text-gray-900">State:</span> {orderDetails.shippingAddress.state}</p>
                    <p><span className="font-medium text-gray-900">Postal Code:</span> {orderDetails.shippingAddress.postalCode}</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Order Items - keeping the existing code */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-4">Order Items ({orderDetails.orderItems?.length || 0})</h4>
            
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orderDetails.orderItems?.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-lg mr-3" />
                          <Link to={`/product/${item.productId}`} className="text-blue-600 hover:underline font-medium">
                            {item.name}
                          </Link>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {item.size && <p>Size: {item.size}</p>}
                        {item.color && <p>Color: {item.color}</p>}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium">Rs. {item.price?.toFixed(2)}</td>
                      <td className="px-4 py-4 text-sm">{item.quantity}</td>
                      <td className="px-4 py-4 text-sm font-semibold">Rs. {(item.price * item.quantity)?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden grid gap-4">
              {orderDetails.orderItems?.map((item, index) => (
                <div key={index} className="bg-white rounded-lg border overflow-hidden">
                  <img src={item.image} alt={item.name} className="w-full h-48 object-cover" />
                  <div className="p-3">
                    <Link to={`/product/${item.productId}`} className="text-sm font-medium hover:text-blue-600 block mb-2">
                      {item.name}
                    </Link>
                    <div className="text-xs text-gray-600 space-y-1 mb-3">
                      {item.size && <p>Size: {item.size}</p>}
                      {item.color && <p>Color: {item.color}</p>}
                      <p>Qty: {item.quantity}</p>
                    </div>
                    <div className="pt-2 border-t flex justify-between items-center">
                      <span className="text-xs text-gray-500">Total</span>
                      <span className="text-sm font-bold">Rs. {(item.price * item.quantity)?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t mt-6 pt-4">
              <div className="flex justify-between items-center bg-white p-4 rounded-lg">
                <span className="text-lg font-semibold">Order Total:</span>
                <span className="text-xl font-bold">Rs. {orderDetails.totalPrice?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancellation Modal */}
      <CancellationModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        orderId={orderDetails._id}
        onSuccess={handleCancellationSuccess}
      />
    </div>
  );
};

export default OrderDetailsPage;
